import React from 'react';
import { IonCheckbox, IonIcon } from '@ionic/react';
import {
  addOutline,
  removeOutline,
  thermometerOutline,
  speedometerOutline,
  rainyOutline,
  compassOutline,
  leafOutline,
  flowerOutline,
} from 'ionicons/icons';
import { COMPONENT_HINT, COMPONENT_LABEL, MAX_CHANNEL, type ComponentKind, type ComponentSelection } from '../../../domain/Component';
import './ComponentPicker.css';

export const COMPONENT_ICON: Record<ComponentKind, string> = {
  air: thermometerOutline,
  pressure: speedometerOutline,
  rain: rainyOutline,
  wind: compassOutline,
  soil: leafOutline,
  valve: flowerOutline,
};

interface Props {
  value: ComponentSelection;
  onChange: (next: ComponentSelection) => void;
}

// Qué tiene enchufado la estación: clima/barómetro/lluvia/viento se tildan, y de
// sondas de suelo y válvulas se elige cuántas.
const ComponentPicker: React.FC<Props> = ({ value, onChange }) => {
  const toggle = (kind: 'air' | 'pressure' | 'rain' | 'wind') => onChange({ ...value, [kind]: !value[kind] });
  const count = (kind: 'soil' | 'valve', delta: number) =>
    onChange({ ...value, [kind]: Math.min(MAX_CHANNEL, Math.max(0, value[kind] + delta)) });

  return (
    <div className="component-picker">
      {(['air', 'pressure', 'rain', 'wind'] as const).map((kind) => (
        <label key={kind} className={`component-option ${value[kind] ? 'selected' : ''}`}>
          <IonIcon icon={COMPONENT_ICON[kind]} className="component-option-icon" />
          <span className="component-option-text">
            <strong>{COMPONENT_LABEL[kind]}</strong>
            <small>{COMPONENT_HINT[kind]}</small>
          </span>
          <IonCheckbox checked={value[kind]} onIonChange={() => toggle(kind)} aria-label={COMPONENT_LABEL[kind]} />
        </label>
      ))}
      {(['soil', 'valve'] as const).map((kind) => (
        <div key={kind} className={`component-option ${value[kind] > 0 ? 'selected' : ''}`}>
          <IonIcon icon={COMPONENT_ICON[kind]} className="component-option-icon" />
          <span className="component-option-text">
            <strong>{kind === 'soil' ? 'Sondas de suelo' : 'Válvulas de riego'}</strong>
            <small>{COMPONENT_HINT[kind]}</small>
          </span>
          <span className="component-stepper">
            <button type="button" onClick={() => count(kind, -1)} disabled={value[kind] === 0} aria-label="Quitar una">
              <IonIcon icon={removeOutline} />
            </button>
            <span aria-live="polite">{value[kind]}</span>
            <button type="button" onClick={() => count(kind, 1)} disabled={value[kind] >= MAX_CHANNEL} aria-label="Agregar una">
              <IonIcon icon={addOutline} />
            </button>
          </span>
        </div>
      ))}
    </div>
  );
};

export default ComponentPicker;
